import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import API_URL from '../api/baseURL';
import SearchableSelect from './SearchableSelect';
import VehicleIdentityCard from './VehicleIdentityCard';
import {
  Panel, Button, Field, Checkbox, ErrorNote, Note,
  inputClass, selectClassSm, textareaClass,
} from './ui/Iparc';
import { fmt } from '../theme/iparc';

// Suggestion relevance labels (cross-vehicle fitment)
const SCOPE_LABELS = {
  'this-vehicle': { txt: 'Ce véhicule', cls: 'text-[#74A98D]' },
  'same-model': { txt: 'Même modèle', cls: 'text-[#C9C3B6]' },
  'same-make': { txt: 'Même marque', cls: 'text-[#8F877B]' },
  'compatible': { txt: 'Compatible', cls: 'text-[#DFA23C]' },
};

// Compact cell input — the item grid needs a denser control than the page form.
const cell = `${inputClass} h-[30px] px-2 text-[12px] rounded-[7px]`;
const cellSelect = selectClassSm;

// Category options (French)
const INVOICE_CATEGORIES = [
  "scanner",
  "vidange",
  "mécanique",
  "électricité",
  "peinture",
  "pièces",
  "main d’œuvre"
];

export default function WorkshopQuotationInvoiceForm({
  type = "quotation",
  token,
  clients,
  vehicles,
  onSubmitted,
  editQuotation = null,   // when set, the form edits this pending quotation
  onCancelEdit,           // called to exit edit mode
}) {
  const { user } = useAuth();
  const [workshopInfo, setWorkshopInfo] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientInfo, setClientInfo] = useState(null);
  const [clientVehicles, setClientVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [items, setItems] = useState([
    { designation: "", reference: "", brand: "", quantity: 1, unitPrice: 0, category: INVOICE_CATEGORIES[0] },
  ]);
  const [note, setNote] = useState("");
  // Auto-set TVA based on workshop profile (details.tvaApplicable === 'Yes')
  const workshopHasTva = user?.details?.tvaApplicable === 'Yes' || user?.tvaApplicable === 'Yes';
  const [includeTax, setIncludeTax] = useState(false);
  const [depositAmount, setDepositAmount] = useState(""); // NEW
  const [discountPercent, setDiscountPercent] = useState(0); // remise %
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // === AI SUGGEST: local state for per-row suggestions (index -> array) ===
  const [rowSuggestions, setRowSuggestions] = useState({});
  const debounceTimers = useRef({});
  // Keyboard highlight: which row + which suggestion is active
  const [activeSuggest, setActiveSuggest] = useState({ row: null, pos: -1 });

  // Auto-enable TVA if workshop has it set to 'Yes'
  useEffect(() => {
    if (workshopHasTva) setIncludeTax(true);
  }, [workshopHasTva]);

  // Pre-fill the form when editing an existing pending quotation
  useEffect(() => {
    if (!editQuotation) return;
    setSelectedClientId(editQuotation.client?._id || editQuotation.client || "");
    setSelectedVehicleId(editQuotation.vehicle?._id || editQuotation.vehicle || "");
    setNote(editQuotation.note || "");
    setDepositAmount(editQuotation.depositAmount ? String(editQuotation.depositAmount) : "");
    setDiscountPercent(Number(editQuotation.discountPercent) || 0);
    setIncludeTax((Number(editQuotation.tax) || 0) > 0);
    setItems(
      (editQuotation.items || []).map((it) => ({
        designation: it.description || "",
        reference: it.reference || "",
        brand: it.brand || "",
        quantity: it.quantity || 1,
        unitPrice: it.price || 0,
        category: it.category || INVOICE_CATEGORIES[0],
      }))
    );
  }, [editQuotation]);

  // Fetch workshop info on load
  useEffect(() => {
    async function fetchWorkshopInfo() {
      if (user?.role === "car workshop" && token) {
        const res = await fetch(`${API_URL}/workshops/${user._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setWorkshopInfo(await res.json());
        }
      }
    }
    fetchWorkshopInfo();
  }, [user, token]);

  // Update selected client info and fetch client vehicles
  useEffect(() => {
    if (selectedClientId) {
      const found = clients.find((c) => c._id === selectedClientId);
      setClientInfo(found || null);

      // Fetch vehicles for this client
      fetch(`${API_URL}/users/${selectedClientId}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => setClientVehicles(data));
      setSelectedVehicleId(""); // Reset vehicle selection if client changes
    } else {
      setClientInfo(null);
      setClientVehicles([]);
      setSelectedVehicleId("");
    }
  }, [selectedClientId, clients, token]);

  // Relever le kilométrage without leaving the devis.
  //
  // Written straight to the Vehicle rather than held in form state: the reading
  // is a fact about the car, not about this document, and PATCH /vehicles/:id
  // is the one place that refuses a reading that goes backwards. The devis then
  // snapshots whatever the vehicle says at creation, so saving here first is
  // what makes the number on the devis right.
  const saveOdometer = async (vehicle, value) => {
    const res = await fetch(`${API_URL}/vehicles/${vehicle._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ odometerReading: value }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || body.message || 'Enregistrement impossible');

    const updated = body.vehicle || { ...vehicle, odometerReading: value, lastOdometerUpdate: new Date() };
    setClientVehicles((prev) => prev.map((v) => (v._id === vehicle._id ? { ...v, ...updated } : v)));
  };

  // Handle item changes (including category)
  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
            ...item,
            [field]:
              field === "quantity" || field === "unitPrice"
                ? Number(value)
                : value,
          }
          : item
      )
    );
  };

  // === AI SUGGEST: helper to fetch suggestions for a row/index ===
  const fetchSuggestions = async (idx, text) => {
    try {
      // only fetch if we have a vehicle and enough characters
      if (!selectedVehicleId || !text || text.trim().length < 3) {
        setRowSuggestions((prev) => ({ ...prev, [idx]: [] }));
        return;
      }

      const url = `${API_URL}/suggest/parts?vehicleId=${selectedVehicleId}&q=${encodeURIComponent(
        text.trim()
      )}&limit=5`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setRowSuggestions((prev) => ({ ...prev, [idx]: [] }));
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setRowSuggestions((prev) => ({ ...prev, [idx]: list }));
    } catch (e) {
      setRowSuggestions((prev) => ({ ...prev, [idx]: [] }));
    }
  };

  // === AI SUGGEST: on-change wrapper for designation with debounce ===
  const handleDesignationChange = (idx, value) => {
    handleItemChange(idx, "designation", value);
    // Only trigger suggestions for likely parts categories (e.g., "pièces") but keep flexible
    const cat = (items[idx]?.category || "").toLowerCase();
    // you can restrict to parts by uncommenting next line:
    // if (cat !== 'pièces' && cat !== 'mécanique') return;

    // debounce per-row
    if (debounceTimers.current[idx]) clearTimeout(debounceTimers.current[idx]);
    debounceTimers.current[idx] = setTimeout(() => {
      fetchSuggestions(idx, value);
    }, 300);
  };

  // === AI SUGGEST: apply a picked suggestion (fills brand/reference) ===
  const applySuggestion = (idx, suggestion) => {
    if (!suggestion) return;
    if (suggestion.brand) handleItemChange(idx, "brand", suggestion.brand);
    if (suggestion.reference) handleItemChange(idx, "reference", suggestion.reference);
    // keep user's designation as-is; optionally you could set to a canonical name
    clearRowSuggestions(idx);
  };

  // === AI SUGGEST: dismiss the dropdown for a row ===
  const clearRowSuggestions = (idx) => {
    setRowSuggestions((prev) => ({ ...prev, [idx]: [] }));
    setActiveSuggest((prev) => (prev.row === idx ? { row: null, pos: -1 } : prev));
  };

  // === AI SUGGEST: keyboard navigation on the designation input ===
  const handleSuggestKeyDown = (idx, e) => {
    const list = rowSuggestions[idx];
    if (!Array.isArray(list) || list.length === 0) return;
    const pos = activeSuggest.row === idx ? activeSuggest.pos : -1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggest({ row: idx, pos: Math.min(pos + 1, list.length - 1) });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggest({ row: idx, pos: Math.max(pos - 1, 0) });
    } else if (e.key === "Enter" && pos >= 0) {
      e.preventDefault();
      applySuggestion(idx, list[pos]);
    } else if (e.key === "Escape") {
      clearRowSuggestions(idx);
    }
  };

  // Add new item row (with category)
  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      { designation: "", reference: "", brand: "", quantity: 1, unitPrice: 0, category: INVOICE_CATEGORIES[0] }
    ]);
  };

  // Add activation fee line item (10,000 FCFA — platform fee)
  const handleAddActivationFee = () => {
    // Prevent adding duplicates
    if (items.some((it) => it.isActivationFee)) {
      setError("Les frais d'activation sont déjà présents sur ce document.");
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        designation: "Activation compte 1PARC",
        reference: "",
        brand: "",
        quantity: 1,
        unitPrice: 10000,
        category: "scanner",
        isActivationFee: true,
      },
    ]);
  };

  // Check if selected client is already activated
  const isClientActivated = clientInfo?.accountActivated === true;

  // Remove item row
  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    // cleanup suggestions & timers for that row
    setRowSuggestions((prev) => {
      const clone = { ...prev };
      delete clone[index];
      return clone;
    });
    if (debounceTimers.current[index]) {
      clearTimeout(debounceTimers.current[index]);
      delete debounceTimers.current[index];
    }
  };

  // Calculate totals
  const subtotal = items.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0
  );
  const tax = includeTax ? subtotal * 0.18 : 0;
  const total = subtotal + tax;

  // Client-facing totals (10% platform markup, then remise) preview
  const discountFactor = 1 - (Number(discountPercent) || 0) / 100;
  const clientSubtotalFull = Math.round(subtotal * 1.10);
  const clientSubtotal = Math.round(clientSubtotalFull * discountFactor);
  const discountAmount = clientSubtotalFull - clientSubtotal;
  const clientTax = includeTax ? Math.round(clientSubtotal * 0.18) : 0;
  const clientTotal = clientSubtotal + clientTax;

  // Real submit handler (now with correct endpoint)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOkMsg("");

    // Backend expects description not designation, and category
    const mappedItems = items.map(
      ({ designation, reference, brand, quantity, unitPrice, category, isActivationFee }) => ({
        description: designation,
        reference,
        brand,
        quantity,
        price: unitPrice,
        category,
        ...(isActivationFee ? { isActivationFee: true } : {}),
      })
    );

    const payload = {
      client: selectedClientId,
      vehicle: selectedVehicleId,
      items: mappedItems,
      note,
      total,
      subtotal,
      tax,
      // add maintenanceLog if needed
    };

    // Only add depositAmount if creating a quotation
    if (type === "quotation" && depositAmount) {
      payload.depositAmount = Number(depositAmount);
    }
    // Remise (quotations only)
    if (type === "quotation") {
      payload.discountPercent = Number(discountPercent) || 0;
    }

    // Edit mode → PATCH the existing pending quote; else create
    const isEdit = Boolean(editQuotation && editQuotation._id);
    const endpoint = isEdit
      ? `${API_URL}/workshop-quotations/${editQuotation._id}/edit`
      : type === "invoice"
        ? `${API_URL}/workshop-invoices/invoice`
        : `${API_URL}/workshop-quotations`;

    try {
      const res = await fetch(endpoint, {
        method: isEdit ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || "Échec de l'envoi");
      }
      const created = await res.json();
      setOkMsg(
        editQuotation
          ? 'Devis mis à jour. Le client est notifié dans l’application.'
          : type === 'invoice' ? 'Facture envoyée.' : 'Devis envoyé au client.'
      );
      setItems([
        { designation: "", reference: "", brand: "", quantity: 1, unitPrice: 0, category: INVOICE_CATEGORIES[0] }
      ]);
      setNote("");
      setSelectedClientId("");
      setSelectedVehicleId("");
      setDepositAmount(""); // Reset deposit
      setDiscountPercent(0); // Reset remise
      setRowSuggestions({}); // clear suggestions
      if (onSubmitted) onSubmitted(created);
      if (editQuotation && onCancelEdit) onCancelEdit();
    } catch (err) {
      setError(err.message || "Échec de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const docLabel = type === 'invoice' ? 'Facture' : 'Devis';

  return (
    <Panel
      title={editQuotation ? `Modifier le ${docLabel.toLowerCase()}` : `Nouveau ${docLabel.toLowerCase()}`}
      action={
        <span className="text-[11px] text-[#6E665C]">
          {workshopInfo?.businessName || user?.businessName || user?.name || 'Atelier'}
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
        {error && <ErrorNote>{error}</ErrorNote>}
        {okMsg && <Note tone="ok">{okMsg}</Note>}
        {editQuotation && (
          <Note tone="warn">
            Modification d'un devis en attente. Le client sera notifié dans l'application (sans SMS).
          </Note>
        )}

        {/* ── Destinataire ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field label="Client" required>
            <SearchableSelect
              options={clients.map(cl => ({ value: cl._id, label: cl.name || cl.username || cl.email }))}
              value={selectedClientId}
              onChange={(val) => setSelectedClientId(val)}
              placeholder="Sélectionner un client"
              required
            />
          </Field>
          <Field
            label="Véhicule"
            required
            hint={selectedClientId && !clientVehicles.length ? 'Ce client n’a aucun véhicule enregistré.' : undefined}
          >
            <SearchableSelect
              options={clientVehicles.map(v => ({
                value: v._id,
                label: `${v.plateNumber} — ${v.brand || ''} ${v.model || ''}`.trim(),
                sublabel: v.vin ? `VIN ${String(v.vin).toUpperCase()}` : undefined,
              }))}
              value={selectedVehicleId}
              onChange={(val) => setSelectedVehicleId(val)}
              placeholder="Sélectionner un véhicule"
              required
              disabled={!clientVehicles.length}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {clientInfo && (
            <div className="rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3">
              <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1">Destinataire</p>
              <p className="text-[13px] text-[#F4F1EA]">
                {clientInfo.name || clientInfo.username || clientInfo.email}
              </p>
              <p className="text-[11.5px] text-[#8F877B] mt-0.5">
                {[clientInfo.email, clientInfo.phone || clientInfo.contactNumber].filter(Boolean).join(' · ')}
              </p>
            </div>
          )}
          {/* VIN stays on screen while the lines are priced — parts are ordered
              against it, not against the plate. */}
          <VehicleIdentityCard
            vehicle={clientVehicles.find(v => v._id === selectedVehicleId)}
            onSaveOdometer={saveOdometer}
          />
        </div>

        {/* ── Lignes ── */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-2.5">
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Lignes</p>
            <div className="flex gap-2">
              <Button as="button" type="button" className="h-[28px] px-2.5 text-[11.5px]" onClick={handleAddItem}>
                + Ligne
              </Button>
              {type === "invoice" && !isClientActivated && !items.some((it) => it.isActivationFee) && (
                <Button
                  as="button" type="button"
                  className="h-[28px] px-2.5 text-[11.5px]"
                  onClick={handleAddActivationFee}
                  title="Ajouter les frais d'activation 1PARC (10 000 FCFA) — le client bénéficiera de diagnostics gratuits et d'un accès complet"
                >
                  + Activation 1PARC
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-[#241F1B] overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: 880 }}>
              <thead>
                <tr className="border-b border-[#241F1B]">
                  {['Catégorie', 'Désignation', 'Référence', 'Marque', 'Qté', 'P.U.', 'Total', ''].map((h, i) => (
                    <th key={i}
                        className="px-2.5 py-2.5 text-left text-[10px] uppercase tracking-[.14em] text-[#6E665C] font-normal whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b border-[#1E1B18] last:border-0">
                    <td className="px-2.5 py-2 w-[130px]">
                      {item.isActivationFee ? (
                        <span className="text-[11px] font-semibold text-[#74A98D]">1PARC</span>
                      ) : (
                        <select
                          className={cellSelect}
                          value={item.category}
                          onChange={e => handleItemChange(idx, "category", e.target.value)}
                          required
                        >
                          {INVOICE_CATEGORIES.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="relative align-top px-2.5 py-2 min-w-[190px]">
                      {item.isActivationFee ? (
                        <span className="text-[12px] text-[#C9C3B6]">{item.designation}</span>
                      ) : (
                        <>
                          <input
                            className={cell}
                            type="text"
                            value={item.designation}
                            onChange={(e) => handleDesignationChange(idx, e.target.value)}
                            onKeyDown={(e) => handleSuggestKeyDown(idx, e)}
                            onBlur={() => setTimeout(() => clearRowSuggestions(idx), 150)}
                            autoComplete="off"
                            required
                          />
                          {/* AI SUGGEST: dropdown */}
                          {Array.isArray(rowSuggestions[idx]) && rowSuggestions[idx].length > 0 && (
                            <div className="absolute z-20 mt-1 w-[270px] max-h-60 overflow-auto rounded-xl border border-[#2B2724] bg-[#141210] shadow-2xl">
                              <div className="flex items-center justify-between px-2.5 py-1.5 text-[10px] text-[#6E665C] border-b border-[#241F1B] sticky top-0 bg-[#141210]">
                                <span>Suggestions · ↑↓ Entrée · Échap</span>
                                <button type="button"
                                        onMouseDown={(e) => { e.preventDefault(); clearRowSuggestions(idx); }}
                                        className="hover:text-[#F4F1EA]">✕</button>
                              </div>
                              {rowSuggestions[idx].map((s, i) => (
                                <button
                                  type="button"
                                  key={i}
                                  onMouseDown={(e) => { e.preventDefault(); applySuggestion(idx, s); }}
                                  className={`block w-full text-left px-2.5 py-1.5 transition-colors ${
                                    activeSuggest.row === idx && activeSuggest.pos === i
                                      ? 'bg-[#1E1B17]' : 'hover:bg-[#1A1613]'
                                  }`}
                                  title={s.reference}
                                >
                                  <div className="text-[11.5px] flex items-center justify-between gap-2 text-[#C9C3B6]">
                                    <span>
                                      <span className="font-medium text-[#F4F1EA]">{s.brand || '—'}</span>
                                      {' · '}
                                      <span className="font-['Space_Grotesk',sans-serif]">{s.reference || '—'}</span>
                                    </span>
                                    {SCOPE_LABELS[s.scope] && (
                                      <span className={`text-[9px] font-semibold uppercase tracking-[.06em] ${SCOPE_LABELS[s.scope].cls}`}>
                                        {SCOPE_LABELS[s.scope].txt}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-[#6E665C] mt-0.5">
                                    {typeof s.distinctVehicles === 'number' && (
                                      <span>vu sur {s.distinctVehicles} véhicule{s.distinctVehicles > 1 ? 's' : ''}</span>
                                    )}
                                    {Array.isArray(s.makes) && s.makes.length > 0 && (
                                      <span> · {s.makes.slice(0, 3).join(', ')}</span>
                                    )}
                                  </div>
                                  {(typeof s.priceMin === 'number' && typeof s.priceMax === 'number') && (
                                    <div className="text-[10px] text-[#6E665C] font-['Space_Grotesk',sans-serif] tabular-nums">
                                      {fmt(s.priceMin)}–{fmt(s.priceMax)} F
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-2.5 py-2 w-[120px]">
                      {!item.isActivationFee && (
                        <input
                          className={cell}
                          type="text"
                          value={item.reference}
                          onChange={(e) => handleItemChange(idx, "reference", e.target.value)}
                        />
                      )}
                    </td>
                    <td className="px-2.5 py-2 w-[120px]">
                      {!item.isActivationFee && (
                        <input
                          className={cell}
                          type="text"
                          value={item.brand}
                          onChange={(e) => handleItemChange(idx, "brand", e.target.value)}
                        />
                      )}
                    </td>
                    <td className="px-2.5 py-2 w-[70px]">
                      <input
                        className={`${cell} text-center`}
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
                        required
                        disabled={item.isActivationFee}
                      />
                    </td>
                    <td className="px-2.5 py-2 w-[110px]">
                      <input
                        className={`${cell} text-right`}
                        type="number"
                        min={0}
                        step={0.01}
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(idx, "unitPrice", e.target.value)}
                        required
                        disabled={item.isActivationFee}
                      />
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap text-right">
                      <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[12.5px] text-[#F4F1EA]">
                        {fmt(item.quantity * item.unitPrice)}
                      </span>
                      <span className="block text-[10px] text-[#6E665C] font-['Space_Grotesk',sans-serif] tabular-nums mt-0.5">
                        client {fmt(Math.round(item.quantity * item.unitPrice * 1.10))}
                      </span>
                    </td>
                    <td className="px-2.5 py-2 text-right">
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(idx)}
                          className="text-[#8F877B] hover:text-[#E2574B] transition-colors p-1"
                          aria-label="Retirer la ligne"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                               strokeWidth="1.8" strokeLinecap="round">
                            <path d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Conditions ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field label="Rapport diagnostic / note" className="sm:col-span-2">
            <textarea
              className={textareaClass}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Diagnostic du véhicule, observations, recommandations…"
            />
          </Field>

          {type === "quotation" && (
            <Field label="Acompte demandé (FCFA)" required>
              <input
                type="number" min="0" className={inputClass}
                value={depositAmount}
                onChange={e => setDepositAmount(e.target.value)}
                placeholder="Ex : 20000"
                required
              />
            </Field>
          )}

          {type === "quotation" && (
            <Field
              label="Remise (%)"
              hint={discountPercent > 0
                ? `−${fmt(discountAmount)} F sur le prix client`
                : undefined}
            >
              <input
                type="number" min="0" max="100" step="1" className={inputClass}
                value={discountPercent}
                onChange={(e) => {
                  const v = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                  setDiscountPercent(v);
                }}
                placeholder="0"
              />
            </Field>
          )}
        </div>

        {/* ── Totaux ── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pt-3.5 border-t border-[#241F1B]">
          {workshopHasTva ? (
            <Checkbox
              label="Inclure la TVA (18 %)"
              checked={includeTax}
              onChange={() => setIncludeTax((v) => !v)}
            />
          ) : <div />}

          <div className="sm:ml-auto w-full sm:w-[260px] flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
              <span className="text-[#8F877B]">Votre revenu</span>
              <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
                {fmt(subtotal)} F
              </span>
            </div>
            {includeTax && (
              <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
                <span className="text-[#8F877B]">TVA (18 %)</span>
                <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#DFA23C]">
                  {fmt(tax)} F
                </span>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
              <span className="text-[#8F877B]">
                Total{workshopHasTva && includeTax ? ' TTC' : ''}
              </span>
              <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
                {fmt(total)} F
              </span>
            </div>
            {discountPercent > 0 && (
              <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
                <span className="text-[#8F877B]">Remise {discountPercent} %</span>
                <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#74A98D]">
                  −{fmt(discountAmount)} F
                </span>
              </div>
            )}
            <div className="pt-2 mt-1 border-t border-[#241F1B] flex items-baseline justify-between gap-6">
              <span className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Prix client</span>
              <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[19px] font-semibold text-[#F4F1EA]">
                {fmt(clientTotal)} <span className="text-[12px] text-[#6E665C]">F</span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2.5">
          <Button as="button" type="submit" variant="primary" disabled={loading}>
            {loading
              ? "Envoi…"
              : editQuotation
                ? "Enregistrer les modifications"
                : type === "invoice"
                  ? "Envoyer la facture"
                  : "Envoyer le devis"}
          </Button>
          {editQuotation && (
            <Button as="button" type="button" onClick={() => { if (onCancelEdit) onCancelEdit(); }}>
              Annuler
            </Button>
          )}
        </div>
      </form>
    </Panel>
  );
}
